import { bundleAssetUrl } from './bundleUrl';
import { canPlayAmbience, isSoundMuted, subscribeAudioSettings } from './soundSettings';
import { subscribeUserGestureUnlock } from './userGestureUnlock';

export const CROWD_AMBIENCE_URL = bundleAssetUrl(
  'audio/ambience',
  'freesound_community-carrousel-du-louvre-50093.mp3',
);
export const CHILDREN_AMBIENCE_URL = bundleAssetUrl(
  'audio/ambience',
  'nematoki-trampoline-children-playing-funny-amusement-park-316706.mp3',
);
export const COASTER_AMBIENCE_URL = bundleAssetUrl(
  'audio/ambience',
  'freesound_community-coaster-01-78512.mp3',
);

/** Music is 0.068. Keep the continuous park beds deliberately subtle. */
export const CROWD_AMBIENCE_GAIN = 0.018;
export const CHILDREN_AMBIENCE_GAIN = 0.014;
export const COASTER_AMBIENCE_GAIN = 0.04;

/** Product decision: 60 seconds of quiet after the ~56s coaster clip finishes. */
export const COASTER_QUIET_GAP_MS = 60_000;
export const MEDIA_AMBIENCE_PRELOAD = 'none' as const;

export type ParkMediaElement = {
  src: string;
  preload: string;
  loop: boolean;
  volume: number;
  paused: boolean;
  currentTime: number;
  play(): Promise<void> | void;
  pause(): void;
  addEventListener(type: 'ended' | 'error', listener: () => void): void;
  removeEventListener(type: 'ended' | 'error', listener: () => void): void;
};

export interface MediaAmbienceHandle {
  muted: boolean;
  playing: boolean;
  setMuted(muted: boolean): void;
  stop(): void;
}

type TimerHandle = ReturnType<typeof globalThis.setTimeout>;

type MediaHost = {
  handles: number;
  outputMuted: boolean;
  unlocked: boolean;
  crowd: ParkMediaElement | null;
  children: ParkMediaElement | null;
  coaster: ParkMediaElement | null;
  coasterActive: boolean;
  coasterTimer: TimerHandle | null;
  coasterEnded: (() => void) | null;
  coasterError: (() => void) | null;
  createElement: (url: string) => ParkMediaElement;
  hasCoaster: () => boolean;
};

let host: MediaHost | null = null;
let unsubscribeSettings: (() => void) | null = null;
let unsubscribeGesture: (() => void) | null = null;

function defaultCreateElement(_url: string): ParkMediaElement {
  return new Audio();
}

function getHost(): MediaHost {
  if (!host) {
    host = {
      handles: 0,
      outputMuted: isSoundMuted(),
      unlocked: false,
      crowd: null,
      children: null,
      coaster: null,
      coasterActive: false,
      coasterTimer: null,
      coasterEnded: null,
      coasterError: null,
      createElement: defaultCreateElement,
      // Park ambience is intentionally independent from the player's current rides.
      // Tests may still inject a predicate to exercise scheduling behavior.
      hasCoaster: () => true,
    };
  }
  return host;
}

function shouldPlay(target: MediaHost): boolean {
  return target.unlocked && !target.outputMuted && canPlayAmbience();
}

function coasterAvailable(target: MediaHost): boolean {
  try {
    return target.hasCoaster();
  } catch {
    return false;
  }
}

function safePlay(element: ParkMediaElement): void {
  try {
    void Promise.resolve(element.play()).catch(() => undefined);
  } catch {
    // Autoplay/media failure stays silent and can recover on a later gesture.
  }
}

function pause(element: ParkMediaElement | null): void {
  if (!element) return;
  try {
    element.pause();
  } catch {
    // Element may already be disposed.
  }
}

function clearCoasterTimer(target: MediaHost): void {
  if (target.coasterTimer === null) return;
  globalThis.clearTimeout(target.coasterTimer);
  target.coasterTimer = null;
}

function createBed(target: MediaHost, url: string, gain: number): ParkMediaElement | null {
  try {
    const element = target.createElement(url);
    element.preload = MEDIA_AMBIENCE_PRELOAD;
    element.loop = true;
    element.volume = gain;
    element.src = url;
    return element;
  } catch {
    return null;
  }
}

function ensureBeds(target: MediaHost): void {
  target.crowd ??= createBed(target, CROWD_AMBIENCE_URL, CROWD_AMBIENCE_GAIN);
  target.children ??= createBed(target, CHILDREN_AMBIENCE_URL, CHILDREN_AMBIENCE_GAIN);
}

function scheduleCoaster(target: MediaHost): void {
  if (!shouldPlay(target) || target.coasterActive || target.coasterTimer !== null) return;
  target.coasterTimer = globalThis.setTimeout(() => {
    target.coasterTimer = null;
    if (!shouldPlay(target)) return;
    if (!coasterAvailable(target)) {
      scheduleCoaster(target);
      return;
    }
    const coaster = ensureCoaster(target);
    if (!coaster) return;
    target.coasterActive = true;
    try {
      coaster.currentTime = 0;
    } catch {
      // Ignore seek failures; playback may still work.
    }
    safePlay(coaster);
  }, COASTER_QUIET_GAP_MS);
}

function onCoasterFinished(target: MediaHost): void {
  target.coasterActive = false;
  scheduleCoaster(target);
}

function ensureCoaster(target: MediaHost): ParkMediaElement | null {
  if (target.coaster) return target.coaster;
  try {
    const element = target.createElement(COASTER_AMBIENCE_URL);
    element.preload = MEDIA_AMBIENCE_PRELOAD;
    element.loop = false;
    element.volume = COASTER_AMBIENCE_GAIN;
    element.src = COASTER_AMBIENCE_URL;
    const ended = () => onCoasterFinished(target);
    const error = () => onCoasterFinished(target);
    element.addEventListener('ended', ended);
    element.addEventListener('error', error);
    target.coaster = element;
    target.coasterEnded = ended;
    target.coasterError = error;
    return element;
  } catch {
    return null;
  }
}

function syncHost(target: MediaHost): void {
  if (!shouldPlay(target)) {
    clearCoasterTimer(target);
    pause(target.crowd);
    pause(target.children);
    pause(target.coaster);
    return;
  }

  ensureBeds(target);

  if (target.crowd) {
    target.crowd.volume = CROWD_AMBIENCE_GAIN;
    if (target.crowd.paused) safePlay(target.crowd);
  }
  if (target.children) {
    target.children.volume = CHILDREN_AMBIENCE_GAIN;
    if (target.children.paused) safePlay(target.children);
  }

  if (!coasterAvailable(target)) {
    if (target.coasterActive) {
      pause(target.coaster);
      target.coasterActive = false;
    }
    scheduleCoaster(target);
    return;
  }

  ensureCoaster(target);
  if (target.coasterActive && target.coaster?.paused) {
    target.coaster.volume = COASTER_AMBIENCE_GAIN;
    safePlay(target.coaster);
  } else if (!target.coasterActive) {
    scheduleCoaster(target);
  }
}

function bindSettings(): void {
  if (unsubscribeSettings) return;
  unsubscribeSettings = subscribeAudioSettings(() => {
    if (host) syncHost(host);
  });
}

function bindGesture(): void {
  if (unsubscribeGesture) return;
  unsubscribeGesture = subscribeUserGestureUnlock(() => {
    if (!host) return;
    host.unlocked = true;
    syncHost(host);
  });
}

function disposeHost(target: MediaHost): void {
  clearCoasterTimer(target);
  pause(target.crowd);
  pause(target.children);
  pause(target.coaster);
  if (target.coaster && target.coasterEnded) {
    target.coaster.removeEventListener('ended', target.coasterEnded);
  }
  if (target.coaster && target.coasterError) {
    target.coaster.removeEventListener('error', target.coasterError);
  }
  for (const element of [target.crowd, target.children, target.coaster]) {
    if (!element) continue;
    try {
      element.src = '';
    } catch {
      // ignore
    }
  }
  target.crowd = null;
  target.children = null;
  target.coaster = null;
  target.coasterActive = false;
  target.coasterEnded = null;
  target.coasterError = null;
  target.unlocked = false;
}

export function createMediaAmbienceChannel(options?: {
  createElement?: (url: string) => ParkMediaElement;
  hasCoaster?: () => boolean;
}): MediaAmbienceHandle {
  const target = getHost();
  target.handles += 1;
  if (options?.createElement) target.createElement = options.createElement;
  if (options?.hasCoaster) target.hasCoaster = options.hasCoaster;
  target.outputMuted = isSoundMuted();
  bindSettings();
  bindGesture();
  syncHost(target);

  const handle: MediaAmbienceHandle = {
    muted: target.outputMuted,
    get playing() {
      return Boolean(host === target && shouldPlay(target) && target.crowd && !target.crowd.paused);
    },
    setMuted(muted: boolean) {
      target.outputMuted = muted;
      handle.muted = muted;
      syncHost(target);
    },
    stop() {
      if (target.handles > 0) target.handles -= 1;
      if (target.handles > 0) return;
      disposeHost(target);
      host = null;
      unsubscribeGesture?.();
      unsubscribeGesture = null;
      unsubscribeSettings?.();
      unsubscribeSettings = null;
    },
  };
  return handle;
}

export function resetMediaAmbienceForTests(): void {
  if (host) {
    disposeHost(host);
    host = null;
  }
  unsubscribeGesture?.();
  unsubscribeGesture = null;
  unsubscribeSettings?.();
  unsubscribeSettings = null;
}
